/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { getAddressString } from "../../../util/helperFunctions";
import Button from "../../../components/Button";
import { ClinicProfileResponse } from "../../../models/clinic/profile/ClinicProfileResponse";
import ClinicLogo from "../../../components/Pictures/Clinic/ClinicLogo";
import { Cog } from "../../../common/Icons/Cog";
import { SectionCard } from "../../../components/Cards/SectionCard";
import { strings } from "../../../common/Strings/Strings";
import { TimePeriodResponse } from "../../../models/clinic/profile/TimePeriodResponse";
import UserProfilePicture from "../../../components/Pictures/User/UserProfilePicture";
import { Envelope } from "../../../common/Icons/Envelope";
import { Telephone } from "../../../common/Icons/Telephone";
import { Globe } from "../../../common/Icons/Globe";
import AlertBox, { AlertType } from "../../../components/AlertBox";
import { ReservationTypeResponse } from "../../../models/reservationtype/ReservationTypeResponse";
import Tag, { TagType } from "../../../components/Tag";

interface Props {
  isSmall?: boolean;
  profile: ClinicProfileResponse;
  showSettingsOption?: boolean;
  userPetId?: string;
}

const ClinicProfile: React.FC<Props> = ({ isSmall = false, profile, showSettingsOption, userPetId }: Props) => {
  const navigate = useNavigate();
  const [reservationType, setReservationType] = useState<ReservationTypeResponse>();
  const [chooseReservationTypeAlert, setChooseReservationTypeAlert] = useState<boolean>(false);

  const getTimePeriodString = (period?: TimePeriodResponse) => {
    if (!period) return strings.clinicClosed;
    return `${period.startTime  } - ${  period.endTime}`;
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-nowrap items-center">
          <div style={{ width: "100px", height: "100px" }}>
            <ClinicLogo clinicId={profile.id} />
          </div>
          <div className="ml-3">
            <div className="text-xl font-bold lg:text-2xl text-zinc-800 dark:text-white">{profile.name}</div>
            {profile.addresses && profile.addresses.map((address) => <div>{getAddressString(address)}</div>)}
          </div>
          <div className="ml-auto" hidden={!profile.editable || !showSettingsOption}>
            <Button variant="icon" onClick={() => navigate(`/clinics/${profile.id}/profile-settings`)}>
              <Cog />
            </Button>
          </div>
        </div>
      </div>
      <div className={isSmall ? "space-y-4" : "grid md:grid-cols-3 gap-4"}>
        <div className="space-y-4">
          <SectionCard
            hidden={!profile.emails?.length && !profile.phones?.length}
            title={strings.clinicProfileContacts}
          >
            <div className="space-y-2">
              {profile.phones &&
                profile.phones.map((phone) => (
                  <div className="flex gap-2">
                    <span className="text-gray-500 dark:text-gray-400">
                      <Telephone variant="outline" />
                    </span>
                    <a href={`tel:${  phone.value}`} className="tw-link-dark font-normal">
                      {phone.value}
                    </a>
                  </div>
                ))}
              {profile.emails &&
                profile.emails.map((email) => (
                  <div className="flex gap-2">
                    <span className="text-gray-500 dark:text-gray-400">
                      <Envelope variant="outline" />
                    </span>
                    <a href={`mailto:${  email.value}`} className="tw-link-dark font-normal">
                      {email.value}
                    </a>
                  </div>
                ))}
              {profile.website && (
                <div className="flex gap-2">
                  <span className="text-gray-500 dark:text-gray-400">
                    <Globe variant="outline" />
                  </span>
                  <a href={profile.website} target="_blank" className="tw-link-dark font-normal" rel="noreferrer">
                    {profile.website}
                  </a>
                </div>
              )}
            </div>
          </SectionCard>
          <SectionCard
            hidden={!profile.openingHours.length && !profile.specialOpeningHours.length}
            title={strings.clinicProfileOpeningHours}
          >
            <table className="w-full">
              {profile.openingHours.map((hours) => (
                <tr>
                  <td>{strings[hours.day]}</td>
                  <td>{getTimePeriodString(hours.open)}</td>
                </tr>
              ))}
              {profile.specialOpeningHours.map((hours) => (
                <tr className="text-red-700 dark:text-red-400">
                  <td>{moment(hours.date).format("MMMM Do")}</td>
                  <td>{getTimePeriodString(hours.open)}</td>
                </tr>
              ))}
            </table>
          </SectionCard>
          <SectionCard hidden={!profile.employees?.length} title={strings.clinicProfileColleagues}>
            <div className="space-y-2">
              {profile.employees &&
                profile.employees.map((employee) => (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10">
                      <UserProfilePicture
                        border
                        borderWidth="2px"
                        userId={employee.userId}
                        pictureId={employee.profilePictureId}
                      />
                    </div>
                    <div>{employee.fullName}</div>
                  </div>
                ))}
            </div>
          </SectionCard>
        </div>
        <div className="md:col-span-2 space-y-4">
          <SectionCard hidden={!profile.introduction} title={strings.clinicProfileIntroduction}>
            <div className="whitespace-pre-line">{profile.introduction}</div>
          </SectionCard>
          <SectionCard hidden={!profile.reservationTypes} title={strings.clinicProfileAvailableServices}>
            {profile.reservationTypes && profile.reservationTypes?.length === 0 ? (
              <AlertBox closeAble={false} message={strings.onlineReservationIsNotAvailable} type={AlertType.INFO} />
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {profile.reservationTypes &&
                    profile.reservationTypes.map((rt) => (
                      <Button
                        variant="basic"
                        onClick={() => {
                          setReservationType(rt);
                        }}
                      >
                        <Tag
                          type={reservationType?.id === rt.id ? TagType.secondary : TagType.secondaryOutlined}
                          text={rt.name}
                        />
                      </Button>
                    ))}
                </div>
                <AlertBox
                  hidden={!chooseReservationTypeAlert}
                  message={strings.pleaseChooseService}
                />
                <div className="mt-6">
                  <Button
                    className="ml-auto"
                    minW={!isSmall}
                    onClick={() => {
                      if (reservationType === undefined) {
                        setChooseReservationTypeAlert(true);
                      } else {
                        navigate(`/reservation/calendar/${userPetId}`, {
                          state: {
                            clinic: profile,
                            resType: reservationType,
                          },
                        });
                      }
                    }}
                  >
                    {strings.newAppointment}
                  </Button>
                </div>
              </>
            )}
          </SectionCard>
          {profile.customSections.map((item) => (
            <SectionCard title={item.title}>
              <div className="whitespace-pre-line">{item.content}</div>
            </SectionCard>
          ))}
        </div>
      </div>
    </>
  );
};

export default ClinicProfile;
